import React, { useState, useEffect } from 'react';
import { X, Type } from 'lucide-react';
import Button from './ui/Button';
import DatePicker from './ui/DatePicker';
import { API_PATH, request } from '../config/api';
import styles from './CreateEventModal.module.css'; // Reuse same styles

const API_URL = `${API_PATH}/events`;

const EditEventModal = ({ isOpen, onClose, event, onUpdated }) => {
    const [eventName, setEventName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const today = new Date().toISOString().split('T')[0];

    // Pre-fill form when event changes
    useEffect(() => {
        if (event) {
            setEventName(event.name || '');
            setStartDate(event.start_date?.slice(0, 10) || '');
            setEndDate(event.end_date?.slice(0, 10) || '');
            setError('');
        }
    }, [event]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!startDate || !endDate) {
            setError('Please select both a start and end date.');
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setError('End date cannot be before start date.');
            return;
        }
        const durationDays = (new Date(endDate) - new Date(startDate)) / (24 * 60 * 60 * 1000);
        if (durationDays > 365) {
            setError('Event duration cannot exceed 365 days.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await request(`${API_URL}/${event.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: eventName, startDate, endDate }),
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update event');

            onUpdated(data);
            onClose();
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen || !event) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <div>
                        <h2 className={styles.modalTitle}>Edit Event</h2>
                        <p className={styles.modalSubtitle}>Update your event details below.</p>
                    </div>
                    <button
                        className={styles.closeBtn}
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className={styles.errorBanner}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zm-.75-8.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                        </svg>
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Event Name</label>
                        <div className={styles.inputWrapper}>
                            <Type size={18} className={styles.inputIcon} aria-hidden />
                            <input
                                type="text"
                                className={styles.input}
                                placeholder="e.g. Annual Tech 2026"
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <p className={styles.fieldHint}>Update the title participants already know from your invitations.</p>
                    </div>

                    <div className={styles.dateRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Start Date</label>
                            <DatePicker
                                value={startDate}
                                onChange={(iso) => {
                                    if (event.hasResponses) return;
                                    setStartDate(iso);
                                    if (endDate && endDate < iso) setEndDate('');
                                }}
                                disabled={isLoading || !!event.hasResponses}
                            />
                            {event.hasResponses && (
                                <p className={styles.fieldHint}>Start date cannot be changed when any poll has votes or responses.</p>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>End Date</label>
                            <DatePicker
                                value={endDate}
                                min={startDate || today}
                                max={startDate ? (() => { const d = new Date(startDate); d.setDate(d.getDate() + 365); return d.toISOString().slice(0, 10); })() : undefined}
                                onChange={(iso) => setEndDate(iso)}
                                disabled={isLoading}
                            />
                        </div>
                        <p className={styles.fieldHint} style={{ gridColumn: '1 / -1' }}>Event can be up to 365 days. Extending the end date costs coins per 4-day block. Shortening may refund coins for polls that have no responses.</p>
                    </div>

                    <div className={styles.buttonGroup}>
                        <Button type="button" variant="secondary" size="sm" className={styles.cancelBtn} onClick={onClose} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" className={styles.createBtn} disabled={isLoading} loading={isLoading}>
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEventModal;
